import { Table } from 'react-dsgov';
import { IAquisicaoListagemResumo } from '../../../../../../interfaces/IAquisicao';
import { BrLoading } from '@govbr-ds/react-components';
import styles from './aquisicaoTableResumo.module.scss';
import { formatCurrency, removeFormatCurrency } from '../../../../../../utils/formatCurrency';
import { formatarNrProcesso } from '../../../../../../utils/formatNrProcesso';
import { formatarDataISOpBR } from '../../../../../../utils/formatDate';
import { formatarCNPJOrCpf } from '../../../../../../utils/formatCNPJOrCPF';

const headersBase = [
  { field: 'notaFiscal', label: 'Nota Fiscal' },
  { field: 'dataEmissaoNF', label: 'Data de Emissão' },
  { field: 'codigoNCM', label: 'Código NCM' },
  { field: 'quantidade', label: 'Quantidade' },
  { field: 'precoUnitario', label: 'Preço Unitário' },
  { field: 'valor', label: 'Valor' },
];

interface IProps {
  data: IAquisicaoListagemResumo[];
  isLoading: boolean;
}

export const AquisicaoTableResumo = ({ data, isLoading }: IProps) => {
  return (
    <div className={styles.customTable}>
      {data.length === 0 && !isLoading ? (
        <Table
          headers={headersBase}
          showPagination={false}
          showDensityButtons={false}
          showSearch={false}
          density='medium'
        >
          <tbody className={styles.noRecords}>
            <tr>
              <td colSpan={headersBase.length}>
                <div>Não há operações para serem exibidas.</div>
              </td>
            </tr>
          </tbody>
        </Table>
      ) : (
        <></> &&
        data.map((row) => {
          const hasTributoCadastrado = row.aquisicao?.componentesBaseDeCalculo?.some((baseCalculo) =>
            baseCalculo?.codigosNCM?.some((codigoNCM) => (codigoNCM.tributosValores?.length ?? 0) > 0)
          );

          const headers = hasTributoCadastrado
            ? [
                ...headersBase,
                { field: 'deducoes', label: 'Deduções' },
                { field: 'totalLiquidoAquisicoes', label: 'Valor Total Líquido de Aquisições' },
              ]
            : headersBase;

          const totalDeducoes =
            row.aquisicao?.componentesBaseDeCalculo?.reduce(
              (accBase, baseCalculo) =>
                accBase +
                baseCalculo.codigosNCM.reduce(
                  (accNcm, codigoNCM) =>
                    accNcm +
                    (codigoNCM.tributosValores?.reduce(
                      (accTributo, item) => accTributo + (Number(item?.valor) || 0),
                      0
                    ) ?? 0),
                  0
                ),
              0
            ) ?? 0;

          const totalLiquidoAquisicoes = parseFloat(row.totais.valorTotalDeOperacao) - totalDeducoes;

          return (
            <div key={row.numeroProcesso} className={styles.container}>
              <div className={styles.tableTitle}>
                <i className={`fas fa-file fa-md  ${styles.columnIcon}`} />
                <div className={styles.tableContentContainer}>
                  <div className={styles.tableContent}>
                    <span>Processo minerário: </span> <p>{formatarNrProcesso(row.numeroProcesso)}</p>
                  </div>
                  <div className={styles.tableContent}>
                    <span>Substância: </span> <p>{row.substancia}</p>
                  </div>
                  <div className={styles.tableContent}>
                    <span>Município/UF: </span>{' '}
                    <p>{row.municipio ? `${row.municipio.nome}/${row.municipio.uf}` : ''}</p>
                  </div>
                  <div className={styles.tableContent}>
                    <span>Estabelecimento Adquirente: </span>{' '}
                    <p>{row.estabelecimentoAdquirente ? formatarCNPJOrCpf(row.estabelecimentoAdquirente) : '-'}</p>
                  </div>
                  <div className={styles.tableContent}>
                    <span>É Rejeito? </span> <p> {row.aquisicao.isRejeito ? 'Sim' : 'Não'} </p>
                  </div>
                  <div className={styles.tableContent}>
                    <span>A base de cálculo é consolidada? </span> <p>{row.aquisicao.isConsolidado ? 'Sim' : 'Não'} </p>
                  </div>
                  <div className={styles.tableContent}>
                    <span>Há indícios de irregularidade? </span>{' '}
                    <p>{row.aquisicao.hasSuspeitaIrregularidade ? 'Sim' : 'Não'}</p>
                  </div>
                  {row.aquisicao.hasSuspeitaIrregularidade && (
                    <div className={styles.tableContent}>
                      <span>Motivo da irregularidade: </span> <p>{row.aquisicao.descricaoSuspeitaIrregularidade}</p>
                    </div>
                  )}
                  {row.aquisicao.dataSolicitacaoRenovacaoPlg && (
                    <div className={styles.tableContent}>
                      <span>Data de renovação do processo minerário: </span>{' '}
                      <p>{formatarDataISOpBR(row.aquisicao.dataSolicitacaoRenovacaoPlg)}</p>
                    </div>
                  )}
                </div>
              </div>

              <Table
                showTopBar={false}
                headers={headers}
                showPagination={false}
                showDensityButtons={false}
                showSearch={false}
                density='medium'
                className={styles.aquisicaoTable}
              >
                {row.aquisicao?.componentesBaseDeCalculo?.map((baseCalculo) => (
                  <tbody key={baseCalculo.notaFiscal}>
                    {baseCalculo.codigosNCM.map((codigoNCM, index) => (
                      <tr key={codigoNCM.codigoNCM}>
                        {index === 0 && (
                          <>
                            <th
                              className={styles.border_right}
                              rowSpan={baseCalculo.codigosNCM.length + 1}
                              scope='rowgroup'
                            >
                              {baseCalculo.notaFiscal || '-'}
                            </th>
                            <th
                              className={styles.border_right}
                              rowSpan={baseCalculo.codigosNCM.length + 1}
                              scope='rowgroup'
                            >
                              {baseCalculo.dataEmissaoNF ? formatarDataISOpBR(baseCalculo.dataEmissaoNF) : ' - '}
                            </th>
                          </>
                        )}
                        {headers.map((header) => {
                          if (header?.field === 'precoUnitario') {
                            if (codigoNCM['quantidade'] === '0') return 'x';
                            return (
                              <td align='center' key={header.field}>
                                {formatCurrency(
                                  (parseFloat(codigoNCM['valor']) / parseFloat(codigoNCM['quantidade'])).toFixed(2)
                                )}
                              </td>
                            );
                          }
                          if (header?.field === 'valor')
                            return (
                              <td align='center' key={header.field}>
                                {formatCurrency(codigoNCM[header.field])}
                              </td>
                            );
                          if (header?.field === 'totalLiquidoAquisicoes')
                            return (
                              <td align='center' key={header.field}>
                                {formatCurrency(
                                  removeFormatCurrency(codigoNCM.valor) -
                                    (codigoNCM.tributosValores?.reduce(
                                      (accTributo, item) => accTributo + (Number(item?.valor) || 0),
                                      0
                                    ) ?? 0)
                                )}
                              </td>
                            );
                          if (header?.field === 'deducoes')
                            return (
                              <td align='center' key={header.field}>
                                {formatCurrency(
                                  (
                                    codigoNCM.tributosValores?.reduce(
                                      (acc, item) => acc + (Number(item?.valor) || 0),
                                      0
                                    ) ?? 0
                                  ).toFixed(2)
                                )}
                              </td>
                            );
                          if (header?.field === 'quantidade')
                            return (
                              <td align='center' key={header.field}>
                                {codigoNCM[header.field].toString().replace('.', ',')} {row.aquisicao.unidadeMedida}
                              </td>
                            );
                          if (header?.field === 'codigoNCM')
                            return (
                              <td align='center' key={header.field}>
                                {codigoNCM[header.field]}
                              </td>
                            );
                          return '';
                        })}
                      </tr>
                    ))}
                  </tbody>
                ))}

                <tfoot className={styles.tableFooter}>
                  <tr>
                    <th>Total</th>
                    <th></th>
                    <th></th>
                    <th>
                      {row.totais.quantidadeTotal.toString().replace('.', ',') + ' ' + row.aquisicao.unidadeMedida}{' '}
                    </th>
                    <th></th>
                    <th>{formatCurrency(row.totais.valorTotalDeOperacao)}</th>
                    {hasTributoCadastrado && (
                      <>
                        <th>{formatCurrency(totalDeducoes.toFixed(2))}</th>
                        <th>{formatCurrency(totalLiquidoAquisicoes)}</th>
                      </>
                    )}
                  </tr>
                </tfoot>
              </Table>
              <div className={styles.observacoes}>
                <div className={styles.tableContent}>
                  <span>Observações: </span>
                  <p> {row.aquisicao.observacoes ?? '-'} </p>
                </div>
              </div>
            </div>
          );
        })
      )}
      {isLoading && (
        <div className={styles.tableLoading}>
          <BrLoading large label='Carregando...' />
        </div>
      )}
    </div>
  );
};
